/** @prettier */
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createSelector } from 'reselect';

import { BETSLIP_PRODUCTS } from '@xanadu/ui-shared/constants';
import { getBetsForRunnerId } from '@xanadu/ui-shared/modules/bets/selectors';
// Selectors
import { selectRunner } from '@xanadu/ui-shared/modules/cashout/actions';
import { subscribeToEvent, unsubscribeToEvent } from '@xanadu/ui-shared/modules/event/actions';
import {
  getInRunningFlagForEventId,
  getStatusForEventId,
} from '@xanadu/ui-shared/modules/event/selectors';
import { subscribeToMarket, unsubscribeToMarket } from '@xanadu/ui-shared/modules/market/actions';
import {
  getMarketAllowLiveBettingFlagForMarketId,
  getMarketForRunnerId,
} from '@xanadu/ui-shared/modules/market/selectors';
import { removeSelectionFromMultipleByCids } from '@xanadu/ui-shared/modules/multiples/actions';
import { updateSingleUnmatchedOfferByUser } from '@xanadu/ui-shared/modules/openOffers/actions';
import { getBestPriceForRunner } from '@xanadu/ui-shared/modules/price/selectors';
import { getCriteriaCategoryIds } from '@xanadu/ui-shared/modules/promotions/manualBonus/selectors';
import { removeOffersByCids } from '@xanadu/ui-shared/modules/unsubmittedOffers/actions';
import { updateDisplayDetails } from '@xanadu/ui-shared/modules/user/actions';

import { MOBILE_BREAKPOINT } from '../../../constants';

import Offer from '../../../components/betslip/Offer';

const mapDispatchToProps = {
  onRemoveMultipleSelection: removeSelectionFromMultipleByCids,
  onRemoveOffer: removeOffersByCids,
  updateDisplayDetails,
  selectRunner,
  subscribeToEvent,
  unsubscribeToEvent,
  subscribeToMarket,
  unsubscribeToMarket,
  updateSingleUnmatchedOfferByUser,
};

const isBackWin = (str) => str === 'back' || str === 'win';

const makeGetRunnerInfo = () =>
  createSelector(
    [
      (state, props) => props.offer.runnerId,
      (state, props) => getBetsForRunnerId(state, props.offer.runnerId, true),
      (state, props) => getBestPriceForRunner(state, props.offer.runnerId, props.offer.side),
      (state, props) => getMarketForRunnerId(state, props.offer.runnerId),
    ],
    (runnerId, bets, bestPrice, market = {}) => {
      const { inRunningFlag } = market;

      // NOTE: While we consider bets from multiple runners for calculations, the functionality
      //       requires at least one bet to be on this runner.
      let cashoutAvailable = false;

      let difference = 0;

      let hasBets = false;
      bets.forEach((bet) => {
        // NOTE: Binary markets require us to consider bets on both sides to determine whether we
        //       can cash out.
        const thisRunner = bet.runnerId === runnerId ? 1 : -1;

        if (thisRunner === 1) {
          hasBets = true;
        }

        // NOTE: One fo the potential figures will be 0 depending on side
        const total = bet.stake + bet.potentialLiability + bet.potentialProfit;

        if (isBackWin(bet.side)) {
          difference += total * thisRunner;
        } else {
          difference -= total * thisRunner;
        }
      });
      cashoutAvailable = hasBets && Math.abs(difference) > 0.1;

      return {
        difference,
        cashoutAvailable,
        inRunningFlag,
        bestPrice,
      };
    }
  );

const makeMapStateToProps = () => {
  const getRunnerInfo = makeGetRunnerInfo();

  const mapStateToProps = (state, ownProps) => {
    const { cashoutAvailable, difference, inRunningFlag, bestPrice } = getRunnerInfo(
      state,
      ownProps
    );

    const { eventId, marketId, sportId, competitionId, locationId } = ownProps.offer;

    const windowWidthSize = state.getIn(['application', 'widthSize']);
    const marketStatus = state.getIn(['market', marketId, 'status']);
    const freeBets = state.getIn(['promotions', 'manualBonus', 'data']);

    const isMobile = windowWidthSize <= MOBILE_BREAKPOINT;

    const bonusCriteriaIds = getCriteriaCategoryIds(state);
    const eventStatus = getStatusForEventId(state, eventId);
    const eventInRunning = getInRunningFlagForEventId(state, eventId);
    const marketWillBeLive = getMarketAllowLiveBettingFlagForMarketId(state, marketId);
    const bonusCriteria =
      !bonusCriteriaIds.size ||
      bonusCriteriaIds.some((criteriaId) =>
        [sportId, competitionId, locationId, eventId].includes(criteriaId)
      );

    const ALLOWED_PRODUCTS = [BETSLIP_PRODUCTS.exchange, BETSLIP_PRODUCTS.betbuilder];

    const eventFreeBets = freeBets.filter(({ criteria, products }) => {
      const nonProductCriteria = criteria.find((c) => c.type !== 'PRODUCT');
      return (
        (!nonProductCriteria ||
          [sportId, competitionId, locationId, eventId].includes(nonProductCriteria.value)) &&
        products.some((p) => ALLOWED_PRODUCTS.includes(p))
      );
    });

    const bonusAllowed = bonusCriteria || eventFreeBets.size > 0;

    return {
      config: state.get('betslip'),
      exchangeType: state.getIn(['user', 'exchangeType']),
      isLoggedIn: state.getIn(['user', 'isLoggedIn']),
      oddsType: state.getIn(['user', 'oddsType']),
      aggregationRule: state.getIn(['openOffers', 'aggregationRule']),
      showBetConfirmation: state.getIn(['user', 'showBetConfirmation']),
      bonusBalance: state.getIn(['user', 'balance', 'bonusFreeFunds']),
      isBetBuilderRestricted: state.getIn(['betBuilder', 'restricted']),
      freeBets: eventFreeBets,
      bonusAllowed,
      eventStatus,
      eventInRunning,
      marketStatus,
      marketWillBeLive,
      cashoutAvailable,
      difference,
      inRunningFlag,
      bestPrice,
      isMobile,
    };
  };

  return mapStateToProps;
};

export default withRouter(connect(makeMapStateToProps, mapDispatchToProps)(Offer));
